<template>
  <transition name="update-announcement">
    <div
      v-if="announcement"
      class="update-announcement-overlay"
      role="presentation"
      @click.self="dismiss"
    >
      <section
        class="update-announcement-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="update-announcement-title"
        aria-describedby="update-announcement-message"
      >
        <button
          type="button"
          class="update-announcement-close"
          :aria-label="$t('close')"
          @click="dismiss"
        >
          <AppIcon name="bi-x-lg" class="bi bi-x-lg" aria-hidden="true" />
        </button>

        <div class="update-announcement-visual" :class="{ 'has-media': hasMedia }">
          <img
            v-if="hasImage"
            :src="announcement.asset_url"
            :alt="announcement.asset_alt || announcement.title"
            @error="mediaFailed = true"
          >
          <video
            v-else-if="hasVideo"
            :src="announcement.asset_url"
            controls
            playsinline
            preload="metadata"
            @error="mediaFailed = true"
          ></video>
          <template v-else>
            <span class="update-announcement-orbit update-announcement-orbit-one"></span>
            <span class="update-announcement-orbit update-announcement-orbit-two"></span>
            <div class="update-announcement-icon">
              <AppIcon name="bi-stars" class="bi bi-stars" />
            </div>
          </template>
        </div>

        <div class="update-announcement-content">
          <span class="update-announcement-label">
            {{ $t('whats_new') }}
          </span>
          <h2 id="update-announcement-title">{{ announcement.title }}</h2>
          <p id="update-announcement-message">{{ announcement.message }}</p>

          <div class="update-announcement-actions">
            <button
              type="button"
              class="update-announcement-dismiss"
              @click="dismiss"
            >
              <span>{{ $t('announcement_acknowledge') }}</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  </transition>
</template>

<script>
import helper from '../helper'

export default {
  name: 'UpdateAnnouncementModal',
  data() {
    return {
      announcement: null,
      mediaFailed: false,
    }
  },
  computed: {
    hasImage() {
      return !this.mediaFailed && this.announcement?.asset_type === 'image' && Boolean(this.announcement.asset_url)
    },
    hasVideo() {
      return !this.mediaFailed && this.announcement?.asset_type === 'video' && Boolean(this.announcement.asset_url)
    },
    hasMedia() {
      return this.hasImage || this.hasVideo
    },
  },
  mounted() {
    this.loadAnnouncement()
  },
  methods: {
    async loadAnnouncement() {
      try {
        const response = await helper.customGet(
          `${process.env.VUE_APP_API}/app-announcements/current`,
          { silent: true }
        )
        this.mediaFailed = false
        this.announcement = response?.data?.data || null
      } catch (error) {
        // An announcement should never prevent the rest of the app from loading.
        this.announcement = null
      }
    },
    dismiss() {
      if (!this.announcement) return

      const announcementId = this.announcement.id
      this.announcement = null

      helper.customPost(
        `${process.env.VUE_APP_API}/app-announcements/${announcementId}/dismiss`,
        {},
        { silent: true }
      )
        .catch(error => {
          if (window.Bugsnag) window.Bugsnag.notify(error)
        })
    },
  },
}
</script>

<style scoped>
.update-announcement-overlay {
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(22, 28, 45, 0.54);
  backdrop-filter: blur(3px);
}

.update-announcement-card {
  position: relative;
  width: min(900px, 100%);
  max-height: calc(100vh - 40px);
  overflow-x: hidden;
  overflow-y: auto;
  padding: 50px 34px 30px;
  border: 1px solid #e5e7eb;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.24);
}

.update-announcement-close {
  position: absolute;
  z-index: 4;
  top: 13px;
  right: 14px;
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease, opacity 0.2s ease;
}

.update-announcement-close:hover:not(:disabled) {
  background: #f3f4f6;
  color: #111827;
  transform: scale(1.04);
}

[dir='rtl'] .update-announcement-close {
  right: auto;
  left: 14px;
}

.update-announcement-visual {
  position: relative;
  display: grid;
  width: 100%;
  min-height: 390px;
  place-items: center;
  overflow: hidden;
  border-radius: 28px;
  background: linear-gradient(135deg, #d35ef4 0%, #f1a6cf 46%, #4d5be7 100%);
}

.update-announcement-visual.has-media {
  width: calc(100% - 56px);
  min-height: 0;
  margin: 0 auto;
  aspect-ratio: 1.9 / 1;
  background: #f3f4f6;
}

.update-announcement-visual img,
.update-announcement-visual video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.update-announcement-visual::after {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 18% 20%, rgba(255, 255, 255, 0.48), transparent 27%),
    radial-gradient(circle at 82% 72%, rgba(255, 205, 211, 0.55), transparent 30%);
  content: '';
}

.update-announcement-visual.has-media::after {
  display: none;
}

.update-announcement-icon {
  position: relative;
  z-index: 2;
  display: grid;
  width: 104px;
  height: 104px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.55);
  border-radius: 30px;
  background: rgba(255, 255, 255, 0.16);
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.24);
  color: #fff;
  font-size: 2.65rem;
  transform: rotate(-4deg);
  backdrop-filter: blur(10px);
}

.update-announcement-orbit {
  position: absolute;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 999px;
}

.update-announcement-orbit-one {
  width: 330px;
  height: 170px;
  transform: rotate(18deg);
}

.update-announcement-orbit-two {
  width: 480px;
  height: 230px;
  transform: rotate(-18deg);
}

.update-announcement-content {
  padding: 30px 0 0;
  text-align: start;
}

.update-announcement-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.update-announcement-content h2 {
  margin: 0 0 16px;
  color: #111827;
  font-size: clamp(2rem, 4vw, 2.55rem);
  font-weight: 850;
  letter-spacing: -0.035em;
  line-height: 1.12;
}

.update-announcement-content p {
  margin: 0;
  color: #374151;
  font-size: 1.08rem;
  line-height: 1.65;
}

.update-announcement-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
}

.update-announcement-dismiss {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  border: 2px solid #3158e7;
  border-radius: 999px;
  background: #fff;
  color: #111827;
  font-weight: 700;
  transition: color 0.2s ease, background 0.2s ease, transform 0.2s ease;
}

.update-announcement-dismiss:hover:not(:disabled) {
  background: #3158e7;
  color: #fff;
  transform: translateY(-1px);
}

.update-announcement-enter-active,
.update-announcement-leave-active {
  transition: opacity 0.25s ease;
}

.update-announcement-enter-active .update-announcement-card,
.update-announcement-leave-active .update-announcement-card {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.25s ease;
}

.update-announcement-enter,
.update-announcement-leave-to {
  opacity: 0;
}

.update-announcement-enter .update-announcement-card,
.update-announcement-leave-to .update-announcement-card {
  opacity: 0;
  transform: translateY(20px) scale(0.96);
}

@media (max-width: 576px) {
  .update-announcement-overlay {
    padding: 16px;
  }

  .update-announcement-visual {
    min-height: 220px;
    border-radius: 20px;
  }

  .update-announcement-visual.has-media {
    width: 100%;
  }

  .update-announcement-card {
    padding: 48px 18px 22px;
    border-radius: 20px;
  }

  .update-announcement-content {
    padding-top: 24px;
  }

  .update-announcement-content h2 {
    font-size: 1.75rem;
  }

  .update-announcement-actions {
    justify-content: stretch;
  }

  .update-announcement-dismiss {
    flex: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .update-announcement-enter-active,
  .update-announcement-leave-active,
  .update-announcement-enter-active .update-announcement-card,
  .update-announcement-leave-active .update-announcement-card,
  .update-announcement-dismiss,
  .update-announcement-close {
    transition: none;
  }
}
</style>
